import React, { useEffect, useRef, useState } from "react";
import { FaceDetection, type Detection } from "@mediapipe/face_detection";

/**
 * Câmera ligada na tela de espera do quiosque (ADR 0043): detecta um rosto no
 * próprio aparelho, a distância, e só tira o quadro de quem mostra intenção de
 * bater o ponto: rosto de frente para a câmera, no centro da imagem e parado
 * por cerca de 1,5 s. Quem só passa na frente do aparelho (de lado, na borda
 * da imagem ou andando) não dispara o reconhecimento. Nada sai do aparelho até
 * esse momento; a detecção roda no navegador.
 *
 * Os arquivos do detector (wasm e modelo de longo alcance, ~5 MB, uma vez por
 * abertura da página) vêm do CDN, na mesma versão fixada no package.json.
 */
const MEDIAPIPE_VERSION = "0.4.1646425229";
const ASSETS_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@${MEDIAPIPE_VERSION}/`;
/** Largura mínima do rosto, em fração da largura do quadro: ~150 px num quadro de 1280, o bastante para a busca. */
const MIN_FACE_WIDTH = 0.12;
/** Quadros seguidos com o rosto de frente, no centro e parado antes de capturar (~1,5 s a 4 quadros por segundo). */
const HOLD_FRAMES = 6;
const DETECT_EVERY_MS = 250;
/** Distância máxima do centro do rosto ao centro da imagem, na horizontal (fração da largura). */
const MAX_OFF_CENTER = 0.22;
/** Giro máximo da cabeça: desvio do nariz em relação ao meio dos olhos, em fração da distância entre os olhos (~20°). */
const MAX_YAW = 0.3;
/** Nariz entre a linha dos olhos e a boca: fora desta faixa a pessoa olha para baixo ou para cima. */
const PITCH_RANGE: [number, number] = [0.2, 0.85];
/** Movimento máximo do rosto entre dois quadros (fração da largura) e variação máxima do tamanho: acima disto a pessoa está andando. */
const MAX_MOVE = 0.035;
const MAX_GROWTH = 0.12;
/** Lado maior do quadro enviado ao servidor. */
const MAX_SIDE = 1024;

type Props = {
  /** Depois do comprovante: só volta a capturar quando a frente da câmera ficar livre, para não repetir a mesma pessoa. */
  waitForClearView: boolean;
  onFrame: (frame: Blob) => void;
  /** Câmera ou detector indisponível: o quiosque cai para o botão "Bater ponto". */
  onUnavailable: (message: string) => void;
};

type Hint = "preparando" | "sem-rosto" | "longe" | "centro" | "olhar" | "varios" | "parado" | "livre";

const HINTS: Record<Hint, string> = {
  preparando: "Preparando a câmera...",
  "sem-rosto": "Aproxime-se e olhe para a câmera",
  longe: "Chegue um pouco mais perto",
  centro: "Fique na frente do aparelho",
  olhar: "Olhe para a câmera",
  varios: "Uma pessoa por vez",
  parado: "Fique parado, olhando para a câmera...",
  livre: "Próximo: aproxime-se e olhe para a câmera",
};

/** Rosto de frente para a câmera, pelos pontos do detector (0 e 1 olhos, 2 nariz, 3 boca). */
function isFacingCamera(d: Detection): boolean {
  const l = d.landmarks;
  if (!l || l.length < 4) return false;
  const eyeMidX = (l[0].x + l[1].x) / 2;
  const eyeMidY = (l[0].y + l[1].y) / 2;
  const eyeDist = Math.abs(l[0].x - l[1].x);
  const eyeToMouth = l[3].y - eyeMidY;
  if (eyeDist <= 0 || eyeToMouth <= 0) return false;
  const yaw = Math.abs(l[2].x - eyeMidX) / eyeDist;
  const pitch = (l[2].y - eyeMidY) / eyeToMouth;
  return yaw <= MAX_YAW && pitch >= PITCH_RANGE[0] && pitch <= PITCH_RANGE[1];
}

const isCentered = (d: Detection) => Math.abs(d.boundingBox.xCenter - 0.5) <= MAX_OFF_CENTER;

export default function FaceWatcher({ waitForClearView, onFrame, onUnavailable }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hint, setHint] = useState<Hint>("preparando");
  const [progress, setProgress] = useState(0);
  const onFrameRef = useRef(onFrame);
  const onUnavailableRef = useRef(onUnavailable);
  onFrameRef.current = onFrame;
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let detector: FaceDetection | null = null;
    let timer: number | null = null;
    let stable = 0;
    let last: Detection["boundingBox"] | null = null;
    let cleared = !waitForClearView;
    let captured = false;
    const video = videoRef.current;

    const stop = () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
      detector?.close().catch(() => undefined);
    };

    const capture = () => {
      if (!video || captured) return;
      captured = true;
      const scale = Math.min(1, MAX_SIDE / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!stopped && blob) onFrameRef.current(blob);
          else captured = false;
        },
        "image/jpeg",
        0.85,
      );
    };

    const reset = (next: Hint) => {
      stable = 0;
      last = null;
      setProgress(0);
      setHint(next);
    };

    const onResults = ({ detections }: { detections: Detection[] }) => {
      if (stopped || captured) return;
      const big = detections.filter((d) => d.boundingBox.width >= MIN_FACE_WIDTH);
      if (!cleared) {
        // Depois do comprovante: espera a frente da câmera ficar livre.
        if (big.length === 0) cleared = true;
        return reset("livre");
      }
      const candidates = big.filter((d) => isCentered(d) && isFacingCamera(d));
      if (candidates.length > 1) return reset("varios");
      if (candidates.length === 0) {
        if (big.some(isCentered)) return reset("olhar");
        if (big.length) return reset("centro");
        return reset(detections.length ? "longe" : "sem-rosto");
      }
      const box = candidates[0].boundingBox;
      const walking =
        last !== null &&
        (Math.hypot(box.xCenter - last.xCenter, box.yCenter - last.yCenter) > MAX_MOVE || Math.abs(box.width / last.width - 1) > MAX_GROWTH);
      stable = walking ? 1 : stable + 1;
      last = box;
      setHint("parado");
      setProgress(Math.min(1, stable / HOLD_FRAMES));
      if (stable >= HOLD_FRAMES) {
        reset("parado");
        capture();
      }
    };

    const loop = async () => {
      if (stopped) return;
      try {
        if (!captured && video && video.readyState >= 2 && detector) await detector.send({ image: video });
      } catch {
        /* quadro perdido: tenta no próximo */
      }
      if (!stopped) timer = window.setTimeout(loop, DETECT_EVERY_MS);
    };

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (stopped || !video) return stream.getTracks().forEach((t) => t.stop());
        video.srcObject = stream;
        await video.play();
      } catch {
        if (!stopped) onUnavailableRef.current("A câmera não está liberada neste aparelho.");
        return;
      }
      try {
        detector = new FaceDetection({ locateFile: (file) => `${ASSETS_URL}${file}` });
        detector.setOptions({ model: "full", minDetectionConfidence: 0.6 });
        detector.onResults(onResults);
        await detector.initialize();
      } catch {
        if (!stopped) onUnavailableRef.current("Não foi possível carregar o detector de rosto.");
        return;
      }
      if (stopped) return;
      setHint(cleared ? "sem-rosto" : "livre");
      loop();
    })();

    return stop;
  }, [waitForClearView]);

  return (
    <div className="kiosk__camera">
      <video ref={videoRef} className="kiosk__video" muted playsInline autoPlay aria-label="Imagem da câmera para o reconhecimento" />
      <div className={`kiosk__cameraHint${hint === "parado" ? " kiosk__cameraHint--ok" : ""}`} aria-live="polite">
        {HINTS[hint]}
        {hint === "parado" ? <span className="kiosk__cameraProgress" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" /> : null}
      </div>
    </div>
  );
}
